import { FC, useMemo } from 'react';
import { Cell, UseTableRowProps } from 'react-table';
import { useRecoilValue } from 'recoil';
import isNumber from 'lodash/isNumber';

import * as K from 'common/constants';
import ELEMENT from 'common/element';
import { DimensionsObject } from 'common/types';
import { Column } from 'common/graphqlTypes';
import { getPQDisplayText, getPQValue, PQDataValue, PQLinkedValue } from 'common/util/pqUtil';
import { numericStringCompare } from 'common/util/string';
import VirtualisedTable, { TableOptions } from 'components/common/Table/VirtualisedTable';
import Table, { TableHeader, TableRow } from 'components/common/Table/Table';
import { timezoneState } from 'components/common/Managers/PreferencesManager.state';
import PowerQueryCell from 'components/search/PowerQueriesPage/components/PowerQueryTable/PowerQueryCell';
import { ColumnFormat } from 'components/search/PowerQueriesPage/types';
import { searchParamState } from '../Managers/RouteStateManager.state';

import './PowerQueryResultTable.scss';

type DisplayValue = { [key: string]: PQLinkedValue };

type PowerQueryResultTableProps = {
  headers: Column[];
  rowValues: PQDataValue[][];
  filterText?: string;
  displayOptions?: TableOptions;
  isNonVirtual?: boolean;
  containerDimensions?: DimensionsObject;
  testId?: ELEMENT;
  dashboardName?: string;
};

// Sort numeric column values
const numberCompare = (a: UseTableRowProps<TableRow>, b: UseTableRowProps<TableRow>, columnId: string): 0 | 1 | -1 => {
  const linkedValueA = a?.original?.[columnId] as unknown as PQLinkedValue;
  const linkedValueB = b?.original?.[columnId] as unknown as PQLinkedValue;
  const valueA = linkedValueA.value || 0;
  const valueB = linkedValueB.value || 0;
  if (valueA < valueB) {
    return -1;
  }
  if (valueA > valueB) {
    return 1;
  }
  return 0;
};

// Sort non-numeric column values. If both values are numbers or null/undefined, hand them to numberCompare().
const stringCompare = (a: UseTableRowProps<TableRow>, b: UseTableRowProps<TableRow>, columnId: string): 0 | 1 | -1 => {
  const linkedValueA = a?.original?.[columnId] as unknown as PQLinkedValue;
  const linkedValueB = b?.original?.[columnId] as unknown as PQLinkedValue;
  const valueA = linkedValueA.value;
  const valueB = linkedValueB.value;
  if ((isNumber(valueA) || valueA == null) && (isNumber(valueB) || valueB == null)) {
    return numberCompare(a, b, columnId);
  }
  return numericStringCompare(String(valueA), String(valueB));
};

/**
 * @prop headers                array of header specification objects
 * @prop rowValues              array of arrays with values for each row
 * @prop filterText             optional lowercase string to filter contents
 * @prop displayOptions         options for table
 * @prop isNonVirtual           if true, do not use infinite scroll
 * @prop containerDimensions    height/width for table container
 * @prop testId                 unique identifier for testing
 * @prop dashboardName          dashboard name of the table panel
 */

const PowerQueryResultTable: FC<PowerQueryResultTableProps> = ({
  headers,
  rowValues,
  filterText,
  displayOptions,
  isNonVirtual,
  containerDimensions,
  testId,
  dashboardName,
}: PowerQueryResultTableProps) => {
  const timezone = useRecoilValue(timezoneState);
  const tabName = useRecoilValue(searchParamState(K.URL_PARAM.DASHBOARD_ACTIVE_TAB));
  const memoizedStringCompare = useMemo(() => stringCompare, []);
  const memoizedNumberCompare = useMemo(() => numberCompare, []);

  // Set up the columns for the table displayer
  const tableHeaders: TableHeader[] = headers.map((header: Column, index: number) => {
    const isNumeric = !!(header.format && header.format.toLowerCase() === ColumnFormat.NUMBER);

    // The first column is always left-aligned, no matter the type
    return {
      content: header.name,
      field: `${index}-${header.name}`,
      isNumeric,
      alignment: index === 0 ? K.ALIGNMENT.START : undefined,
      renderCell: ({ value }: Cell<PQLinkedValue>): JSX.Element => (
        <PowerQueryCell cell={value} header={headers[index]} />
      ),
      sortMethod: isNumeric ? memoizedNumberCompare : memoizedStringCompare,
    };
  });

  // Dashboards support filtering rows by text value
  const filteredData = !filterText
    ? rowValues
    : rowValues.filter((rowValue) => {
        for (let i = 0; i < headers.length; i += 1) {
          if (getPQDisplayText(rowValue[i], headers[i], { timezone }).toLowerCase().includes(filterText)) {
            return true;
          }
        }
        return false;
      });

  // Set up the rows for the table displayer
  const data = filteredData.map((row: PQDataValue[]) => {
    const rowObject: DisplayValue = {};
    row.forEach((value, idx) => {
      const header = headers[idx];
      const fieldName = header.name;
      // In Workloads tab of "Kuberneted explorer" built-in dashboard, format cell values to a link by checking for the separator between label and url in the string
      if (
        dashboardName === K.KUBERNETES_EXPLORER &&
        tabName === K.WORKLOAD_TAB &&
        header.name === K.WORKLOADS_LINK_HEADER
      ) {
        const newString = (value as unknown as PQLinkedValue)?.value as string;
        const arr = newString ? newString.split(K.WORKLOADS_CELL_LINK_SEPARATOR) : [];
        value = { url: arr[1], value: arr[0] } as PQLinkedValue;
      }
      // Ensure column value is in PQLinkedValue format.
      // Our custom sorting and custom Cell renderer depend on it.
      const url = (value as unknown as PQLinkedValue)?.url || null;
      const pqValue = getPQValue(value);
      const displayValue = pqValue != null ? pqValue : '';
      rowObject[`${idx}-${fieldName}`] = { value: displayValue, url };
    });
    return rowObject;
  });

  const tableOptions = {
    isStriped: false,
    isSortable: false,
    hoverRow: true,
    sidePadding: 10,
    autoSizeColumns: true,
  };

  // Dashboards use the non-virtual form
  return isNonVirtual ? (
    <Table headers={tableHeaders} data={data} options={{ ...tableOptions, ...displayOptions }} testId={testId} />
  ) : (
    <VirtualisedTable
      headers={tableHeaders}
      data={data}
      options={tableOptions}
      containerDimensions={containerDimensions}
      testId={testId}
    />
  );
};

export default PowerQueryResultTable;
